import { memo } from "react";
import {
  Medal,
  Trophy,
  Bot,
  Brain,
  Languages,
  Database,
  BarChart2,
  BarChart3,
  PieChart,
  Workflow,
  Plug,
  Radio,
  Network,
  RefreshCw,
  Handshake,
  Search,
  PenLine,
  Users,
  Palette,
} from "lucide-react";
import {
  SiPython,
  SiR,
  SiPostgresql,
  SiApacheairflow,
  SiFastapi,
  SiDocker,
  SiHtml5,
  SiNodedotjs,
  SiSpringboot,
  SiReact,
  SiStreamlit,
  SiArduino,
  SiGit,
  SiGithub,
  SiSap,
  SiJira,
  SiCloudflare,
  SiLangchain,
  SiTensorflow,
  SiPytorch,
  SiOpencv,
} from "react-icons/si";
import { useLanguage } from "../context/LanguageContext";
import skillsData from "../data/skills.json";

// Map skill name → icon component (Lucide for generic, Simple Icons for brands)
const SKILL_ICONS = {
  // AI & Machine Learning
  "Agentic RAG": Bot,
  "LLM Fine-tuning": Brain,
  "LangChain / LangGraph": SiLangchain,
  "NLP & Text Embeddings": Languages,
  TensorFlow: SiTensorflow,
  PyTorch: SiPytorch,
  OpenCV: SiOpencv,
  // Data & Analytics
  Python: SiPython,
  R: SiR,
  SQL: Database,
  PostgreSQL: SiPostgresql,
  "Power BI": BarChart2,
  Tableau: BarChart3,
  KNIME: Workflow,
  "Apache Airflow": SiApacheairflow,
  "Data Pipelines": PieChart,
  // Engineering & Development
  FastAPI: SiFastapi,
  Docker: SiDocker,
  "HTML/CSS": SiHtml5,
  "Node.js": SiNodedotjs,
  "Spring Boot": SiSpringboot,
  "React / TypeScript": SiReact,
  Streamlit: SiStreamlit,
  "REST APIs & SSE": Plug,
  MQTT: Radio,
  Arduino: SiArduino,
  // Tools & Platforms
  Git: SiGit,
  GitHub: SiGithub,
  "SAP S/4HANA": SiSap,
  Jira: SiJira,
  "Cloudflare & Vercel": SiCloudflare,
  "Adobe Photoshop & Lightroom": Palette,
  // Product & Delivery
  "Product Management": Network,
  "Agile / Scrum": RefreshCw,
  "Stakeholder Management": Handshake,
  "User Research": Search,
  "Technical Writing": PenLine,
  "Cross-functional Leadership": Users,
};

/**
 * Skills section — three category grids + certifications list.
 * Data comes from src/data/skills.json.
 * Category names and certification titles switch to *_de variants when German is active.
 */
const SkillsSection = memo(() => {
  const { lang, t } = useLanguage();
  const sk = t.skills;
  const { categories, certifications, achievements } = skillsData;

  return (
    <section id="skills" className="section">
      <div className="section-overlay" />
      <div className="container">
        <div className="divider" />

        <div style={{ marginBottom: 46 }}>
          <p className="section-tag fade-up">{sk.tag}</p>
          <h2 className="section-title fade-up fade-up-delay-1">{sk.title}</h2>
        </div>

        {/* ── Skill category grids ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 34 }}>
          {categories.map((cat, ci) => {
            const catName =
              lang === "de" && cat.name_de ? cat.name_de : cat.name;
            return (
              <div key={cat.name} className={`fade-up fade-up-delay-${ci + 1}`}>
                {/* Category label */}
                <div
                  style={{
                    fontFamily: "'DM Mono',monospace",
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    marginBottom: 13,
                  }}
                >
                  <span style={{ color: "var(--accent)" }}>{"// "}</span>
                  {catName}
                </div>

                {/* Icon grid */}
                <div className="skills-grid">
                  {cat.skills.map((s) => {
                    const Ic = SKILL_ICONS[s.name];
                    return (
                      <div key={s.name} className="skill-item">
                        <span className="skill-icon">
                          {Ic ? (
                            <Ic size={26} style={{ color: "var(--text)" }} />
                          ) : (
                            s.icon
                          )}
                        </span>
                        <span style={{ color: "var(--text)", fontSize: 11 }}>
                          {s.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Certifications & Achievements ──────────────────────────────────── */}
        <div className="fade-up" style={{ marginTop: 48 }}>
          <div
            style={{
              fontFamily: "'DM Mono',monospace",
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "var(--text-muted)",
              marginBottom: 20,
            }}
          >
            <span style={{ color: "var(--accent)" }}>{"// "}</span>
            {sk.certificationsLabel}
          </div>

          {/* Certifications sub-section */}
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "var(--text-muted)",
              fontFamily: "'DM Mono',monospace",
              marginBottom: 10,
            }}
          >
            {sk.certsSubLabel}
          </div>
          <div
            className="certs-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 11,
              marginBottom: 28,
            }}
          >
            {certifications.map((c) => {
              const certTitle =
                lang === "de" && c.title_de ? c.title_de : c.title;
              return (
                <div
                  key={c.title}
                  className="card"
                  style={{ padding: "13px 17px" }}
                >
                  <div
                    style={{
                      fontSize: "0.84rem",
                      fontWeight: 600,
                      marginBottom: 3,
                    }}
                  >
                    <Medal
                      size={13}
                      style={{
                        display: "inline",
                        verticalAlign: "middle",
                        marginRight: 6,
                        color: "var(--accent)",
                      }}
                    />
                    {certTitle}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--accent)",
                      fontFamily: "'DM Mono',monospace",
                    }}
                  >
                    {c.org}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Achievements sub-section */}
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: "var(--text-muted)",
              fontFamily: "'DM Mono',monospace",
              marginBottom: 10,
            }}
          >
            {sk.achievementsSubLabel}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            {achievements.map((a) => {
              const achTitle =
                lang === "de" && a.title_de ? a.title_de : a.title;
              const achDesc =
                lang === "de" && a.description_de
                  ? a.description_de
                  : a.description;
              const achBadge =
                lang === "de" && a.badge_de ? a.badge_de : a.badge;
              return (
                <div
                  key={a.title}
                  className="card"
                  style={{ padding: "16px 20px" }}
                >
                  <div
                    style={{
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    <Trophy
                      size={13}
                      style={{
                        display: "inline",
                        verticalAlign: "middle",
                        marginRight: 6,
                        color: "var(--accent)",
                      }}
                    />
                    {achTitle}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--accent)",
                      fontFamily: "'DM Mono',monospace",
                      marginBottom: 6,
                    }}
                  >
                    {a.org}
                  </div>
                  {achDesc && (
                    <p
                      style={{
                        fontSize: "0.78rem",
                        color: "var(--text-muted)",
                        lineHeight: 1.55,
                        margin: 0,
                      }}
                    >
                      {achDesc}
                    </p>
                  )}
                  {achBadge && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 10,
                        color: "var(--accent)",
                        fontFamily: "'DM Mono',monospace",
                        background: "rgba(168,85,247,0.08)",
                        border: "1px solid var(--border)",
                        borderRadius: 4,
                        padding: "3px 8px",
                      }}
                    >
                      <Medal size={10} />
                      {achBadge}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
});

export default SkillsSection;
